import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import * as adminService from '../../services/adminService';
import { UserHealthCard } from '../../components/admin/UserHealthCard';
import { getSmallThumbnailUrl } from '../../utils/mediaUrl';
import './AdminUserDetailPage.css';

interface UserDetail {
  id: string;
  username: string;
  fullName?: string;
  email: string;
  avatarUrl?: string;
  role: string;
  status: string;
  subscriptionStatus: string;
  accountType?: string;
  emailVerified?: boolean;
  stripeCustomerId?: string;
  createdAt: string;
  lastLoginAt?: string;
  trialEndsAt?: string;
}

type SupportAction = 'weeklyReport' | 'passwordReset' | 'verifyEmail';
type SupportStatus = { state: 'idle' } | { state: 'sending' } | { state: 'success'; message: string } | { state: 'error'; message: string };

type ActivityTab =
  | 'notes'
  | 'shadowing'
  | 'dictation'
  | 'comprehension'
  | 'quiz'
  | 'savedVocab'
  | 'sightWord'
  | 'chat'
  | 'writing';

interface NoteItem {
  id: string;
  courseId: string | null;
  courseTitle: string | null;
  associationType: string;
  content: string;
  vocabularyWord: string | null;
  phraseText: string | null;
  sentenceIndex: number | null;
  timestamp: number | null;
  isPinned: boolean;
  isStarred: boolean;
  tags: string[];
  createdAt: string;
}

interface ShadowingItem {
  id: string;
  courseId: string;
  courseTitle: string | null;
  sentenceIndex: number;
  sentenceText: string;
  mode: string;
  scriptVisible: boolean;
  overallScore: number;
  accuracyScore: number;
  fluencyScore: number;
  completenessScore: number;
  prosodyScore: number | null;
  recognizedText: string | null;
  recordingUrl: string | null;
  durationMs: number | null;
  createdAt: string;
}

interface DictationItem {
  id: string;
  courseId: string;
  courseTitle: string | null;
  sentenceIndex: number;
  expectedWord: string;
  userInput: string;
  isCorrect: boolean;
  inputMode: string;
  createdAt: string;
}

interface AuditItem {
  id: string;
  admin_id: string;
  action: string;
  target_type: string;
  target_id: string | null;
  reason: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

interface TranslationErrorItem {
  type: 'vocabulary' | 'grammar' | 'structure';
  description: string;
  relatedWord?: string;
  relatedGrammar?: string;
}

interface ComprehensionItem {
  id: string;
  courseId: string;
  courseTitle: string | null;
  sentenceIndex: number;
  originalSentence: string;
  correctTranslation: string;
  userTranslation: string;
  locale: string;
  overallScore: number;
  isCorrect: boolean;
  errors: TranslationErrorItem[];
  feedback: string;
  source: 'ai' | 'rejected';
  createdAt: string;
}

interface QuizItem {
  id: string;
  courseId: string;
  courseTitle: string | null;
  batchId: string | null;
  questionId: string;
  questionType: string;
  isCorrect: boolean;
  relatedWords: string[];
  timeSpentSeconds: number | null;
  batchTotalQuestions: number | null;
  batchCorrectAnswers: number | null;
  createdAt: string;
}

interface SavedVocabItem {
  id: string;
  vocabularyId: string;
  word: string | null;
  lemma: string | null;
  encounteredForm: string | null;
  masteryLevel: number;
  reviewCount: number;
  reps: number;
  lapses: number;
  state: number;
  stability: number;
  difficulty: number;
  due: string | null;
  isFavorite: boolean;
  lastReviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface SightWordItem {
  id: string;
  word: string;
  correctCount: number;
  errorCount: number;
  totalAttempts: number;
  accuracy: number | null;
  lastSeenAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ChatProfileItem {
  id: string;
  botUserId: string;
  botUsername: string | null;
  personaName: string | null;
  personaType: string | null;
  conversationId: string | null;
  messageCount: number;
  userMessageCount: number;
  botMessageCount: number;
  familiarityLevel: string;
  lastUserMessageAt: string | null;
  lastBotMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface WritingItem {
  id: string;
  courseId: string | null;
  courseTitle: string | null;
  type: string;
  vocabularySource: string | null;
  contentType: string | null;
  status: string;
  currentGate: number;
  totalScore: number;
  tokensConsumed: number;
  pointsEarned: number;
  timeSpentSeconds: number;
  isPerfect: boolean;
  scenarioTheme: string | null;
  startedAt: string;
  completedAt: string | null;
  createdAt: string;
}

interface EnrollmentItem {
  id: string;
  courseId: string;
  courseTitle: string | null;
  courseThumbnailUrl: string | null;
  cefrLevel: string | null;
  difficultyLevel: string | null;
  enrolledAt: string;
  lastAccessedAt: string | null;
  completedAt: string | null;
  completed: boolean;
  progressPercentage: number;
  totalWatchTimeSeconds: number;
  paymentMethod: string | null;
  paymentAmount: number | null;
}

interface ActivityState<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  loading: boolean;
  error: string | null;
}

interface Subscription {
  status: string;
  platform?: string;
  startedAt?: string;
  expiresAt?: string;
  trialEndsAt?: string;
  product?: { sku?: string; billingPeriod?: string; name?: string };
}

interface Transaction {
  id: string;
  createdAt: string;
  amount: number;
  currency?: string;
  status: string;
  type: string;
}

interface TokenTransaction {
  id: string;
  createdAt: string;
  amount: number;
  type: string;
  description: string;
  balanceAfter: number;
}

interface LearningStats {
  totalSessions: number;
  totalDurationSeconds: number;
  uniqueCourses: number;
  lastSessionAt?: string;
}

interface EnrollmentStats {
  totalEnrollments: number;
  completedCourses: number;
  totalWatchTime: number;
}

interface TokenStats {
  totalEarned: number;
  totalSpent: number;
  loginStreak: number;
  bestLoginStreak: number;
}

interface UserDetailResponse {
  user: UserDetail;
  subscription: Subscription | null;
  recentTransactions: Transaction[];
  tokenBalance: number;
  tokenStats: TokenStats | null;
  recentTokenTransactions: TokenTransaction[];
  learningStats: LearningStats;
  enrollmentStats: EnrollmentStats;
}

type ModalType = 'role' | 'suspend' | 'tokens' | 'accountType' | null;

const AdminUserDetailPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<UserDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Modal state
  const [selectedRole, setSelectedRole] = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  const [tokenAmount, setTokenAmount] = useState('');
  const [tokenReason, setTokenReason] = useState('');
  const [selectedAccountType, setSelectedAccountType] = useState('regular');
  const [accountTypeReason, setAccountTypeReason] = useState('');

  // Support-action inline status (one per action button)
  const [supportStatus, setSupportStatus] = useState<Record<SupportAction, SupportStatus>>({
    weeklyReport: { state: 'idle' },
    passwordReset: { state: 'idle' },
    verifyEmail: { state: 'idle' },
  });

  // Activity tabs (notes / shadowing / dictation) — read-only
  const [activeTab, setActiveTab] = useState<ActivityTab>('notes');
  const [notesState, setNotesState] = useState<ActivityState<NoteItem>>({
    items: [], total: 0, page: 1, limit: 25, loading: false, error: null,
  });
  const [shadowingState, setShadowingState] = useState<ActivityState<ShadowingItem>>({
    items: [], total: 0, page: 1, limit: 25, loading: false, error: null,
  });
  const [dictationState, setDictationState] = useState<ActivityState<DictationItem>>({
    items: [], total: 0, page: 1, limit: 50, loading: false, error: null,
  });
  const [auditState, setAuditState] = useState<ActivityState<AuditItem>>({
    items: [], total: 0, page: 1, limit: 25, loading: false, error: null,
  });
  const [comprehensionState, setComprehensionState] = useState<ActivityState<ComprehensionItem>>({
    items: [], total: 0, page: 1, limit: 25, loading: false, error: null,
  });
  const [quizState, setQuizState] = useState<ActivityState<QuizItem>>({
    items: [], total: 0, page: 1, limit: 25, loading: false, error: null,
  });
  const [savedVocabState, setSavedVocabState] = useState<ActivityState<SavedVocabItem>>({
    items: [], total: 0, page: 1, limit: 25, loading: false, error: null,
  });
  const [sightWordState, setSightWordState] = useState<ActivityState<SightWordItem>>({
    items: [], total: 0, page: 1, limit: 50, loading: false, error: null,
  });
  const [chatState, setChatState] = useState<ActivityState<ChatProfileItem>>({
    items: [], total: 0, page: 1, limit: 25, loading: false, error: null,
  });
  const [writingState, setWritingState] = useState<ActivityState<WritingItem>>({
    items: [], total: 0, page: 1, limit: 25, loading: false, error: null,
  });
  const [enrollmentsState, setEnrollmentsState] = useState<ActivityState<EnrollmentItem>>({
    items: [], total: 0, page: 1, limit: 10, loading: false, error: null,
  });
  const enrollmentsLoadedRef = useRef(false);
  const [activityCounts, setActivityCounts] = useState<adminService.UserActivityCounts | null>(null);
  const [health, setHealth] = useState<adminService.UserHealth | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);
  // Default the heavy "Learning Activity" tab grid to collapsed — health card
  // already surfaces the engagement signal, so admins only need to dig in
  // when something looks off.
  const [activityExpanded, setActivityExpanded] = useState(false);
  const notesLoadedRef = useRef(false);
  const shadowingLoadedRef = useRef(false);
  const dictationLoadedRef = useRef(false);
  const auditLoadedRef = useRef(false);
  const comprehensionLoadedRef = useRef(false);
  const quizLoadedRef = useRef(false);
  const savedVocabLoadedRef = useRef(false);
  const sightWordLoadedRef = useRef(false);
  const chatLoadedRef = useRef(false);
  const writingLoadedRef = useRef(false);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await adminService.getUserDetail(id);
      setData(res);
      setSelectedRole(res.user.role);
    } catch {
      setError(t('userDetail.fetchError', 'Failed to load user details'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateTime = (dateStr?: string): string => {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatLearningTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const formatCurrency = (amount: number, currency?: string): string => {
    return `${currency || 'USD'} $${Math.abs(amount).toFixed(2)}`;
  };

  const getInitial = (name?: string): string => (name || '?').charAt(0).toUpperCase();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleChangeRole = async () => {
    if (!id || !data) return;
    setActionLoading(true);
    try {
      await adminService.changeUserRole(id, selectedRole);
      setData({ ...data, user: { ...data.user, role: selectedRole } });
      setActiveModal(null);
    } catch {
      // error handled silently
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!id || !data) return;
    setActionLoading(true);
    try {
      await adminService.suspendUser(id, suspendReason);
      setData({ ...data, user: { ...data.user, status: 'suspended' } });
      setActiveModal(null);
      setSuspendReason('');
    } catch {
      // error handled silently
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!id || !data) return;
    setActionLoading(true);
    try {
      await adminService.activateUser(id);
      setData({ ...data, user: { ...data.user, status: 'active' } });
    } catch {
      // error handled silently
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeAccountType = async () => {
    if (!id || !data) return;
    setActionLoading(true);
    try {
      await adminService.changeAccountType(id, {
        accountType: selectedAccountType,
        reason: accountTypeReason.trim() || undefined,
      });
      setData({ ...data, user: { ...data.user, accountType: selectedAccountType } });
      setActiveModal(null);
      setAccountTypeReason('');
    } catch {
      // error handled silently
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdjustTokens = async () => {
    if (!id || !data) return;
    const amount = parseInt(tokenAmount, 10);
    if (isNaN(amount) || amount === 0 || !tokenReason.trim()) return;
    setActionLoading(true);
    try {
      const res = await adminService.adjustUserTokens(id, { amount, reason: tokenReason });
      setData({
        ...data,
        tokenBalance: res.newBalance ?? data.tokenBalance,
      });
      setActiveModal(null);
      setTokenAmount('');
      setTokenReason('');
    } catch {
      // error handled silently
    } finally {
      setActionLoading(false);
    }
  };

  const runSupportAction = async (
    action: SupportAction,
    call: () => Promise<unknown>,
    successMsg: string,
  ) => {
    setSupportStatus((s) => ({ ...s, [action]: { state: 'sending' } }));
    try {
      await call();
      setSupportStatus((s) => ({ ...s, [action]: { state: 'success', message: successMsg } }));
      window.setTimeout(() => {
        setSupportStatus((s) =>
          s[action].state === 'success' ? { ...s, [action]: { state: 'idle' } } : s,
        );
      }, 5000);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string; error?: { message?: string } } } })
          ?.response?.data?.error?.message ||
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as { message?: string })?.message ||
        'Request failed';
      setSupportStatus((s) => ({ ...s, [action]: { state: 'error', message: msg } }));
    }
  };

  const handleResendWeeklyReport = () => {
    if (!id) return;
    runSupportAction(
      'weeklyReport',
      () => adminService.resendWeeklyReport(id),
      t('userDetail.weeklyReportSent', 'Weekly report regenerated and emailed.'),
    );
  };

  const handleSendPasswordReset = () => {
    if (!id) return;
    runSupportAction(
      'passwordReset',
      () => adminService.sendPasswordResetEmail(id),
      t('userDetail.passwordResetSent', 'Password reset email sent.'),
    );
  };

  const handleSendVerificationEmail = () => {
    if (!id) return;
    runSupportAction(
      'verifyEmail',
      () => adminService.sendVerificationEmail(id),
      t('userDetail.verificationEmailSent', 'Verification email sent.'),
    );
  };

  const fetchNotes = useCallback(async (page = 1) => {
    if (!id) return;
    setNotesState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await adminService.getUserNotes(id, { page, limit: 25 });
      setNotesState({
        items: res.items || [],
        total: res.total || 0,
        page: res.page || page,
        limit: res.limit || 25,
        loading: false,
        error: null,
      });
      notesLoadedRef.current = true;
    } catch {
      setNotesState((s) => ({ ...s, loading: false, error: t('userDetail.loadFailed', 'Failed to load.') }));
    }
  }, [id, t]);

  const fetchShadowing = useCallback(async (page = 1) => {
    if (!id) return;
    setShadowingState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await adminService.getUserShadowingAttempts(id, { page, limit: 25 });
      setShadowingState({
        items: res.items || [],
        total: res.total || 0,
        page: res.page || page,
        limit: res.limit || 25,
        loading: false,
        error: null,
      });
      shadowingLoadedRef.current = true;
    } catch {
      setShadowingState((s) => ({ ...s, loading: false, error: t('userDetail.loadFailed', 'Failed to load.') }));
    }
  }, [id, t]);

  const fetchDictation = useCallback(async (page = 1) => {
    if (!id) return;
    setDictationState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await adminService.getUserDictationAttempts(id, { page, limit: 50 });
      setDictationState({
        items: res.items || [],
        total: res.total || 0,
        page: res.page || page,
        limit: res.limit || 50,
        loading: false,
        error: null,
      });
      dictationLoadedRef.current = true;
    } catch {
      setDictationState((s) => ({ ...s, loading: false, error: t('userDetail.loadFailed', 'Failed to load.') }));
    }
  }, [id, t]);

  const fetchAudit = useCallback(async (page = 1) => {
    if (!id) return;
    setAuditState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await adminService.getUserAuditLog(id, { page, limit: 25 });
      setAuditState({
        items: res.items || [],
        total: res.total || 0,
        page: res.page || page,
        limit: res.limit || 25,
        loading: false,
        error: null,
      });
      auditLoadedRef.current = true;
    } catch {
      setAuditState((s) => ({ ...s, loading: false, error: t('userDetail.loadFailed', 'Failed to load.') }));
    }
  }, [id, t]);

  const fetchComprehension = useCallback(async (page = 1) => {
    if (!id) return;
    setComprehensionState((s) => ({ ...s, loading: true, error: null }));
    try {
      // Per-attempt audit (raw translation + score + AI feedback) — replaces
      // the older course-level progress view. Course-level is course count;
      // admins want the actual sentence the learner produced.
      const res = await adminService.getUserComprehensionTranslationAttempts(id, { page, limit: 25 });
      setComprehensionState({
        items: res.items || [],
        total: res.total || 0,
        page: res.page || page,
        limit: res.limit || 25,
        loading: false,
        error: null,
      });
      comprehensionLoadedRef.current = true;
    } catch {
      setComprehensionState((s) => ({ ...s, loading: false, error: t('userDetail.loadFailed', 'Failed to load.') }));
    }
  }, [id, t]);

  const fetchQuiz = useCallback(async (page = 1) => {
    if (!id) return;
    setQuizState((s) => ({ ...s, loading: true, error: null }));
    try {
      // Per-question attempts grouped by batchId. Replaces the old
      // course-level quiz progress summary.
      const res = await adminService.getUserQuizAttempts(id, { page, limit: 25 });
      setQuizState({
        items: res.items || [],
        total: res.total || 0,
        page: res.page || page,
        limit: res.limit || 25,
        loading: false,
        error: null,
      });
      quizLoadedRef.current = true;
    } catch {
      setQuizState((s) => ({ ...s, loading: false, error: t('userDetail.loadFailed', 'Failed to load.') }));
    }
  }, [id, t]);

  const fetchSavedVocab = useCallback(async (page = 1) => {
    if (!id) return;
    setSavedVocabState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await adminService.getUserSavedVocabulary(id, { page, limit: 25 });
      setSavedVocabState({
        items: res.items || [],
        total: res.total || 0,
        page: res.page || page,
        limit: res.limit || 25,
        loading: false,
        error: null,
      });
      savedVocabLoadedRef.current = true;
    } catch {
      setSavedVocabState((s) => ({ ...s, loading: false, error: t('userDetail.loadFailed', 'Failed to load.') }));
    }
  }, [id, t]);

  const fetchSightWord = useCallback(async (page = 1) => {
    if (!id) return;
    setSightWordState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await adminService.getUserSightWordMastery(id, { page, limit: 50 });
      setSightWordState({
        items: res.items || [],
        total: res.total || 0,
        page: res.page || page,
        limit: res.limit || 50,
        loading: false,
        error: null,
      });
      sightWordLoadedRef.current = true;
    } catch {
      setSightWordState((s) => ({ ...s, loading: false, error: t('userDetail.loadFailed', 'Failed to load.') }));
    }
  }, [id, t]);

  const fetchChat = useCallback(async (page = 1) => {
    if (!id) return;
    setChatState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await adminService.getUserChatProfiles(id, { page, limit: 25 });
      setChatState({
        items: res.items || [],
        total: res.total || 0,
        page: res.page || page,
        limit: res.limit || 25,
        loading: false,
        error: null,
      });
      chatLoadedRef.current = true;
    } catch {
      setChatState((s) => ({ ...s, loading: false, error: t('userDetail.loadFailed', 'Failed to load.') }));
    }
  }, [id, t]);

  const fetchEnrollments = useCallback(async (page = 1) => {
    if (!id) return;
    setEnrollmentsState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await adminService.getUserEnrollments(id, { page, limit: 10 });
      setEnrollmentsState({
        items: res.items || [],
        total: res.total || 0,
        page: res.page || page,
        limit: res.limit || 10,
        loading: false,
        error: null,
      });
      enrollmentsLoadedRef.current = true;
    } catch {
      setEnrollmentsState((s) => ({ ...s, loading: false, error: t('userDetail.loadFailed', 'Failed to load.') }));
    }
  }, [id, t]);

  const fetchWriting = useCallback(async (page = 1) => {
    if (!id) return;
    setWritingState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await adminService.getUserWritingSessions(id, { page, limit: 25 });
      setWritingState({
        items: res.items || [],
        total: res.total || 0,
        page: res.page || page,
        limit: res.limit || 25,
        loading: false,
        error: null,
      });
      writingLoadedRef.current = true;
    } catch {
      setWritingState((s) => ({ ...s, loading: false, error: t('userDetail.loadFailed', 'Failed to load.') }));
    }
  }, [id, t]);

  useEffect(() => {
    if (activeTab === 'notes' && !notesLoadedRef.current && !notesState.loading) {
      fetchNotes(1);
    } else if (activeTab === 'shadowing' && !shadowingLoadedRef.current && !shadowingState.loading) {
      fetchShadowing(1);
    } else if (activeTab === 'dictation' && !dictationLoadedRef.current && !dictationState.loading) {
      fetchDictation(1);
    } else if (activeTab === 'comprehension' && !comprehensionLoadedRef.current && !comprehensionState.loading) {
      fetchComprehension(1);
    } else if (activeTab === 'quiz' && !quizLoadedRef.current && !quizState.loading) {
      fetchQuiz(1);
    } else if (activeTab === 'savedVocab' && !savedVocabLoadedRef.current && !savedVocabState.loading) {
      fetchSavedVocab(1);
    } else if (activeTab === 'sightWord' && !sightWordLoadedRef.current && !sightWordState.loading) {
      fetchSightWord(1);
    } else if (activeTab === 'chat' && !chatLoadedRef.current && !chatState.loading) {
      fetchChat(1);
    } else if (activeTab === 'writing' && !writingLoadedRef.current && !writingState.loading) {
      fetchWriting(1);
    }
  }, [
    activeTab,
    fetchNotes, fetchShadowing, fetchDictation,
    fetchComprehension, fetchQuiz, fetchSavedVocab, fetchSightWord, fetchChat, fetchWriting,
    notesState.loading, shadowingState.loading, dictationState.loading,
    comprehensionState.loading, quizState.loading, savedVocabState.loading,
    sightWordState.loading, chatState.loading, writingState.loading,
  ]);

  useEffect(() => {
    if (!auditLoadedRef.current && !auditState.loading) {
      fetchAudit(1);
    }
    if (!enrollmentsLoadedRef.current && !enrollmentsState.loading) {
      fetchEnrollments(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    adminService
      .getUserActivityCounts(id)
      .then((counts) => { if (!cancelled) setActivityCounts(counts); })
      .catch(() => { /* badges silently stay at lazy-fetched totals */ });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setHealthLoading(true);
    adminService
      .getUserHealth(id)
      .then((h) => { if (!cancelled) { setHealth(h); setHealthLoading(false); } })
      .catch(() => { if (!cancelled) setHealthLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const getRoleBadgeClass = (role: string): string => {
    if (role === 'admin') return 'admin-user-detail-badge admin-user-detail-badge--purple';
    return 'admin-user-detail-badge admin-user-detail-badge--gray';
  };

  const getStatusBadgeClass = (status: string): string => {
    if (status === 'active') return 'admin-user-detail-badge admin-user-detail-badge--green';
    if (status === 'suspended') return 'admin-user-detail-badge admin-user-detail-badge--red';
    return 'admin-user-detail-badge admin-user-detail-badge--gray';
  };

  const getSubBadgeClass = (status: string): string => {
    if (status === 'active') return 'admin-user-detail-badge admin-user-detail-badge--green';
    if (status === 'trialing') return 'admin-user-detail-badge admin-user-detail-badge--yellow';
    if (status === 'cancelled') return 'admin-user-detail-badge admin-user-detail-badge--red';
    return 'admin-user-detail-badge admin-user-detail-badge--gray';
  };

  const getAccountTypeBadge = (type?: string): { cls: string; label: string } | null => {
    if (!type || type === 'regular') return null;
    if (type === 'internal') return { cls: 'admin-user-detail-badge admin-user-detail-badge--purple', label: 'Internal' };
    if (type === 'comp') return { cls: 'admin-user-detail-badge admin-user-detail-badge--yellow', label: 'Comp' };
    if (type === 'beta_tester') return { cls: 'admin-user-detail-badge admin-user-detail-badge--green', label: 'Beta' };
    return { cls: 'admin-user-detail-badge admin-user-detail-badge--gray', label: type };
  };

  if (loading) {
    return (
      <div className="admin-user-detail-page">
        <div className="admin-user-detail-header">
          <button className="admin-user-detail-back" onClick={() => navigate('/admin/users')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {t('common.back', 'Back')}
          </button>
        </div>
        <div className="admin-user-detail-loading">
          <div className="admin-user-detail-spinner" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="admin-user-detail-page">
        <div className="admin-user-detail-header">
          <button className="admin-user-detail-back" onClick={() => navigate('/admin/users')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {t('common.back', 'Back')}
          </button>
        </div>
        <div className="admin-user-detail-error">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <p>{error || t('userDetail.notFound', 'User not found')}</p>
          <button className="admin-user-detail-retry-btn" onClick={fetchDetail}>
            {t('common.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  const { user, subscription, recentTransactions, learningStats, enrollmentStats, tokenBalance, tokenStats, recentTokenTransactions } = data;

  return (
    <div className="admin-user-detail-page">
      {/* Header */}
      <div className="admin-user-detail-header">
        <button className="admin-user-detail-back" onClick={() => navigate('/admin/users')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          {t('common.back', 'Back')}
        </button>
        <h1 className="admin-user-detail-title">{t('userDetail.title', 'User Detail')}</h1>
      </div>

      {/* User Health Card — top-of-page summary */}
      <UserHealthCard health={health} loading={healthLoading} />

      {/* Profile Card - full width */}
      <div className="admin-user-detail-card admin-user-detail-card--full">
        <div className="admin-user-detail-card-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          {t('userDetail.profile', 'Profile')}
        </div>
        <div className="admin-user-detail-profile">
          <div className="admin-user-detail-avatar">
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt={user.username} />
            ) : (
              <span>{getInitial(user.fullName || user.username)}</span>
            )}
          </div>
          <div className="admin-user-detail-profile-info">
            {user.fullName && <div className="admin-user-detail-fullname">{user.fullName}</div>}
            <div className="admin-user-detail-username">@{user.username}</div>
            <div className="admin-user-detail-email">{user.email}</div>
          </div>
        </div>
        <div className="admin-user-detail-badges">
          <span className={getRoleBadgeClass(user.role)}>{user.role}</span>
          <span className={getStatusBadgeClass(user.status)}>{user.status}</span>
          {(() => {
            const b = getAccountTypeBadge(user.accountType);
            return b ? <span className={b.cls}>{b.label}</span> : null;
          })()}
        </div>
        <div className="admin-user-detail-meta">
          <div className="admin-user-detail-meta-row">
            <span className="admin-user-detail-meta-label">{t('userDetail.joined', 'Joined')}</span>
            <span className="admin-user-detail-meta-value">{formatDate(user.createdAt)}</span>
          </div>
          <div className="admin-user-detail-meta-row">
            <span className="admin-user-detail-meta-label">{t('userDetail.lastLogin', 'Last Login')}</span>
            <span className="admin-user-detail-meta-value">{formatDateTime(user.lastLoginAt)}</span>
          </div>
        </div>
        <div className="admin-user-detail-actions">
          <button
            className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
            onClick={() => { setSelectedRole(user.role); setActiveModal('role'); }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            {t('userDetail.changeRole', 'Change Role')}
          </button>
          <button
            className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
            onClick={() => {
              setSelectedAccountType(user.accountType || 'regular');
              setAccountTypeReason('');
              setActiveModal('accountType');
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 7L9 18l-5-5" />
            </svg>
            {t('userDetail.changeAccountType', 'Account Type')}
          </button>
          {user.status === 'suspended' ? (
            <button
              className="admin-user-detail-action-btn admin-user-detail-action-btn--green"
              onClick={handleActivate}
              disabled={actionLoading}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              {t('userDetail.activate', 'Activate')}
            </button>
          ) : (
            <button
              className="admin-user-detail-action-btn admin-user-detail-action-btn--red"
              onClick={() => { setSuspendReason(''); setActiveModal('suspend'); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
              </svg>
              {t('userDetail.suspend', 'Suspend')}
            </button>
          )}
        </div>
      </div>

      {/* Section: 帳號與付費 */}
      <div className="admin-user-detail-section-header">
        {t('userDetail.sectionAccountBilling', 'Account & Billing')}
      </div>

      {/* Subscription + Tokens + Recent Token Transactions - merged */}
      <div className="admin-user-detail-card admin-user-detail-card--full">
        <div className="admin-user-detail-card-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
            <line x1="1" y1="10" x2="23" y2="10" />
          </svg>
          {t('userDetail.subscriptionAndTokens', 'Subscription & Tokens')}
        </div>

        {/* Subscription details */}
        <div className="admin-user-detail-subsection">
          <div className="admin-user-detail-subsection-title">
            {t('userDetail.subscription', 'Subscription')}
          </div>
          <div className="admin-user-detail-sub-status">
            <span className={getSubBadgeClass(subscription?.status || user.subscriptionStatus || 'inactive')}>
              {subscription?.status || user.subscriptionStatus || 'inactive'}
            </span>
          </div>
          <div className="admin-user-detail-meta">
            <div className="admin-user-detail-meta-row">
              <span className="admin-user-detail-meta-label">{t('userDetail.plan', 'Plan')}</span>
              <span className="admin-user-detail-meta-value">{subscription?.product?.name || subscription?.product?.sku || '--'}</span>
            </div>
            <div className="admin-user-detail-meta-row">
              <span className="admin-user-detail-meta-label">{t('userDetail.billing', 'Billing')}</span>
              <span className="admin-user-detail-meta-value">{subscription?.product?.billingPeriod || '--'}</span>
            </div>
            <div className="admin-user-detail-meta-row">
              <span className="admin-user-detail-meta-label">{t('userDetail.started', 'Started')}</span>
              <span className="admin-user-detail-meta-value">{formatDate(subscription?.startedAt)}</span>
            </div>
            <div className="admin-user-detail-meta-row">
              <span className="admin-user-detail-meta-label">{t('userDetail.expires', 'Expires')}</span>
              <span className="admin-user-detail-meta-value">{formatDate(subscription?.expiresAt)}</span>
            </div>
            {user.stripeCustomerId && (
              <div className="admin-user-detail-meta-row">
                <span className="admin-user-detail-meta-label">{t('userDetail.stripeId', 'Stripe ID')}</span>
                <span className="admin-user-detail-meta-value admin-user-detail-copyable" onClick={() => copyToClipboard(user.stripeCustomerId!)}>
                  {user.stripeCustomerId}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </span>
              </div>
            )}
            {(subscription?.trialEndsAt || user.trialEndsAt) && (
              <div className="admin-user-detail-meta-row">
                <span className="admin-user-detail-meta-label">{t('userDetail.trialEnds', 'Trial Ends')}</span>
                <span className="admin-user-detail-meta-value">{formatDate(subscription?.trialEndsAt || user.trialEndsAt)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Tokens */}
        <div className="admin-user-detail-subsection">
          <div className="admin-user-detail-subsection-title">
            {t('userDetail.tokens', 'Tokens')}
          </div>
          <div className="admin-user-detail-token-grid admin-user-detail-token-grid--3">
            <div className="admin-user-detail-token-stat">
              <span className="admin-user-detail-token-value admin-user-detail-token-value--primary">
                {tokenBalance.toLocaleString()}
              </span>
              <span className="admin-user-detail-token-label">{t('userDetail.balance', 'Balance')}</span>
            </div>
            <div className="admin-user-detail-token-stat">
              <span className="admin-user-detail-token-value admin-user-detail-token-value--green">
                {(tokenStats?.totalEarned ?? 0).toLocaleString()}
              </span>
              <span className="admin-user-detail-token-label">{t('userDetail.totalEarned', 'Total Earned')}</span>
            </div>
            <div className="admin-user-detail-token-stat">
              <span className="admin-user-detail-token-value admin-user-detail-token-value--red">
                {(tokenStats?.totalSpent ?? 0).toLocaleString()}
              </span>
              <span className="admin-user-detail-token-label">{t('userDetail.totalSpent', 'Total Spent')}</span>
            </div>
          </div>
          <button
            className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
            onClick={() => { setTokenAmount(''); setTokenReason(''); setActiveModal('tokens'); }}
            style={{ marginTop: 16 }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {t('userDetail.adjustTokens', 'Adjust Tokens')}
          </button>
        </div>

        {/* Recent Token Transactions */}
        <div className="admin-user-detail-subsection">
          <div className="admin-user-detail-subsection-title">
            {t('userDetail.recentTokenTransactions', 'Recent Token Transactions')}
          </div>
          {recentTokenTransactions && recentTokenTransactions.length > 0 ? (
            <div className="admin-user-detail-table-wrap">
              <table className="admin-user-detail-table">
                <thead>
                  <tr>
                    <th>{t('userDetail.date', 'Date')}</th>
                    <th>{t('userDetail.amount', 'Amount')}</th>
                    <th>{t('userDetail.type', 'Type')}</th>
                    <th>{t('userDetail.description', 'Description')}</th>
                    <th>{t('userDetail.balanceAfter', 'Balance After')}</th>
                  </tr>
                </thead>
                <tbody>
                  {recentTokenTransactions.slice(0, 5).map((tx) => (
                    <tr key={tx.id}>
                      <td>{formatDate(tx.createdAt)}</td>
                      <td className={tx.amount >= 0 ? 'admin-user-detail-text-green' : 'admin-user-detail-text-red'}>
                        {tx.amount >= 0 ? '+' : ''}{tx.amount.toLocaleString()}
                      </td>
                      <td>{tx.type}</td>
                      <td>{tx.description}</td>
                      <td>{tx.balanceAfter.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="admin-user-detail-empty-text">{t('userDetail.noTokenTransactions', 'No recent token transactions')}</p>
          )}
        </div>
      </div>

      {/* Recent Transactions Table — sits beside Subscription & Tokens
          since it's part of the billing story (not learning). */}
      <div className="admin-user-detail-card admin-user-detail-card--full">
        <div className="admin-user-detail-card-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="1" x2="12" y2="23" />
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
          </svg>
          {t('userDetail.recentTransactions', 'Recent Transactions')}
        </div>
        {recentTransactions && recentTransactions.length > 0 ? (
          <div className="admin-user-detail-table-wrap">
            <table className="admin-user-detail-table">
              <thead>
                <tr>
                  <th>{t('userDetail.date', 'Date')}</th>
                  <th>{t('userDetail.amount', 'Amount')}</th>
                  <th>{t('userDetail.status', 'Status')}</th>
                  <th>{t('userDetail.type', 'Type')}</th>
                </tr>
              </thead>
              <tbody>
                {recentTransactions.slice(0, 5).map((tx) => (
                  <tr key={tx.id}>
                    <td>{formatDate(tx.createdAt)}</td>
                    <td>{formatCurrency(tx.amount, tx.currency)}</td>
                    <td>
                      <span className={`admin-user-detail-badge admin-user-detail-badge--${tx.status === 'succeeded' ? 'green' : tx.status === 'failed' ? 'red' : 'gray'}`}>
                        {tx.status}
                      </span>
                    </td>
                    <td>{tx.type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="admin-user-detail-empty-text">{t('userDetail.noTransactions', 'No recent transactions')}</p>
        )}
      </div>

      {/* Section: 學習進展 */}
      <div className="admin-user-detail-section-header">
        {t('userDetail.sectionLearning', 'Learning Progress')}
      </div>

      {/* Learning Stats Card - full width */}
      <div className="admin-user-detail-card admin-user-detail-card--full">
        <div className="admin-user-detail-card-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
          </svg>
          {t('userDetail.learningStats', 'Learning Stats')}
        </div>
        <div className="admin-user-detail-stats-grid">
          <div className="admin-user-detail-stat-item">
            <span className="admin-user-detail-stat-value">
              {formatLearningTime(learningStats?.totalDurationSeconds || 0)}
            </span>
            <span className="admin-user-detail-stat-label">{t('userDetail.totalTime', 'Total Learning Time')}</span>
          </div>
          <div className="admin-user-detail-stat-item">
            <span className="admin-user-detail-stat-value">{learningStats?.totalSessions || 0}</span>
            <span className="admin-user-detail-stat-label">{t('userDetail.totalSessions', 'Total Sessions')}</span>
          </div>
          <div className="admin-user-detail-stat-item">
            <span className="admin-user-detail-stat-value">{learningStats?.uniqueCourses || 0}</span>
            <span className="admin-user-detail-stat-label">{t('userDetail.uniqueCourses', 'Unique Courses')}</span>
          </div>
          <div className="admin-user-detail-stat-item">
            <span className="admin-user-detail-stat-value">{enrollmentStats?.totalEnrollments || 0}</span>
            <span className="admin-user-detail-stat-label">{t('userDetail.coursesEnrolled', 'Courses Enrolled')}</span>
          </div>
          <div className="admin-user-detail-stat-item">
            <span className="admin-user-detail-stat-value">{enrollmentStats?.completedCourses || 0}</span>
            <span className="admin-user-detail-stat-label">{t('userDetail.completedCourses', 'Completed')}</span>
          </div>
          <div className="admin-user-detail-stat-item">
            <span className="admin-user-detail-stat-value">{tokenStats?.loginStreak ?? 0}</span>
            <span className="admin-user-detail-stat-label">{t('userDetail.loginStreak', 'Login Streak')}</span>
          </div>
        </div>
      </div>

      {/* Enrolled Courses */}
      <div className="admin-user-detail-card admin-user-detail-card--full">
        <div className="admin-user-detail-card-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          </svg>
          {t('userDetail.enrolledCourses', 'Enrolled Courses')}
          {enrollmentsState.total > 0 && (
            <span style={{ marginLeft: 12, fontSize: '0.85rem', color: '#888', fontWeight: 'normal' }}>
              · {enrollmentsState.total}
            </span>
          )}
        </div>
        {enrollmentsState.loading ? (
          <div className="admin-user-detail-loading"><div className="admin-user-detail-spinner" /></div>
        ) : enrollmentsState.error ? (
          <p className="admin-user-detail-empty-text">{enrollmentsState.error}</p>
        ) : enrollmentsState.items.length === 0 ? (
          <p className="admin-user-detail-empty-text">
            {t('userDetail.noEnrollments', 'No enrolled courses yet')}
          </p>
        ) : (
          <>
            <div className="admin-user-detail-table-wrap">
              <table className="admin-user-detail-table">
                <thead>
                  <tr>
                    <th>{t('userDetail.course', 'Course')}</th>
                    <th>{t('userDetail.enrollLevel', 'Level')}</th>
                    <th>{t('userDetail.enrollProgress', 'Progress')}</th>
                    <th>{t('userDetail.enrollWatchTime', 'Watch Time')}</th>
                    <th>{t('userDetail.enrollEnrolledAt', 'Enrolled')}</th>
                    <th>{t('userDetail.enrollLastAccessedAt', 'Last Accessed')}</th>
                    <th>{t('userDetail.enrollStatus', 'Status')}</th>
                    <th>{t('userDetail.enrollPayment', 'Payment')}</th>
                  </tr>
                </thead>
                <tbody>
                  {enrollmentsState.items.map((e) => (
                    <tr key={e.id}>
                      <td>
                        <a
                          href={`/courses/${e.courseId}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#667eea', textDecoration: 'none' }}
                        >
                          {e.courseThumbnailUrl ? (
                            <img
                              src={getSmallThumbnailUrl(e.courseThumbnailUrl)}
                              alt=""
                              style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
                              onError={(ev) => {
                                (ev.currentTarget as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <div style={{ width: 40, height: 40, borderRadius: 6, background: '#eef0fd', flexShrink: 0 }} />
                          )}
                          <span className="admin-user-detail-cell-wrap">{e.courseTitle || e.courseId}</span>
                        </a>
                      </td>
                      <td>{e.cefrLevel || e.difficultyLevel || '--'}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
                          <div style={{ flex: 1, height: 6, background: '#eef0fd', borderRadius: 3, overflow: 'hidden' }}>
                            <div
                              style={{
                                width: `${Math.min(100, Math.max(0, e.progressPercentage))}%`,
                                height: '100%',
                                background: e.completed ? '#10b981' : '#667eea',
                              }}
                            />
                          </div>
                          <span style={{ fontSize: '0.8rem', color: '#666', minWidth: 36, textAlign: 'right' }}>
                            {Math.round(e.progressPercentage)}%
                          </span>
                        </div>
                      </td>
                      <td>{formatLearningTime(e.totalWatchTimeSeconds || 0)}</td>
                      <td>{formatDate(e.enrolledAt)}</td>
                      <td>{e.lastAccessedAt ? formatDateTime(e.lastAccessedAt) : '--'}</td>
                      <td>
                        {e.completed ? (
                          <span className="admin-user-detail-badge admin-user-detail-badge--green">
                            {t('userDetail.enrollCompleted', 'Completed')}
                          </span>
                        ) : (
                          <span className="admin-user-detail-badge admin-user-detail-badge--gray">
                            {t('userDetail.enrollInProgress', 'In Progress')}
                          </span>
                        )}
                      </td>
                      <td>
                        {e.paymentMethod ? (
                          <span className="admin-user-detail-badge admin-user-detail-badge--gray">
                            {e.paymentMethod}
                            {e.paymentAmount !== null && e.paymentAmount > 0 ? ` $${e.paymentAmount.toFixed(2)}` : ''}
                          </span>
                        ) : (
                          '--'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {enrollmentsState.total > enrollmentsState.limit && (
              <div className="admin-user-detail-pager">
                <button
                  disabled={enrollmentsState.page <= 1 || enrollmentsState.loading}
                  onClick={() => fetchEnrollments(enrollmentsState.page - 1)}
                  className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                >
                  {t('common.prev', 'Prev')}
                </button>
                <span className="admin-user-detail-pager-info">
                  {t('userDetail.pageInfo', 'Page {{page}} of {{total}}', {
                    page: enrollmentsState.page,
                    total: Math.max(1, Math.ceil(enrollmentsState.total / enrollmentsState.limit)),
                  })}
                </span>
                <button
                  disabled={enrollmentsState.page * enrollmentsState.limit >= enrollmentsState.total || enrollmentsState.loading}
                  onClick={() => fetchEnrollments(enrollmentsState.page + 1)}
                  className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                >
                  {t('common.next', 'Next')}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Activity Card 1 - 學習活動 (read-only) — collapsed by default */}
      <div className="admin-user-detail-card admin-user-detail-card--full">
        <div className="admin-user-detail-card-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </svg>
          <span>{t('userDetail.cardLearningActivity', 'Learning Activity (read-only)')}</span>
          {!activityExpanded && activityCounts && (
            <span className="admin-user-detail-activity-summary">
              {(activityCounts.comprehension + activityCounts.quiz + activityCounts.shadowing + activityCounts.dictation + activityCounts.notes + activityCounts.savedVocab + activityCounts.sightWord + activityCounts.chat + activityCounts.writing).toLocaleString()}
              {' '}
              {t('userDetail.activitySummaryRecords', 'records')}
            </span>
          )}
          <button
            type="button"
            className="admin-user-detail-collapse-btn"
            onClick={() => setActivityExpanded((v) => !v)}
          >
            {activityExpanded
              ? t('userDetail.hideActivityDetails', 'Hide details')
              : t('userDetail.showActivityDetails', 'Show details')}
          </button>
        </div>

        {activityExpanded && (
          <>

        {/* Sub-group: 課程練習 */}
        <div className="admin-user-detail-tab-group">
          <div className="admin-user-detail-tab-group-label">
            {t('userDetail.groupCoursePractice', 'Course Practice')}
          </div>
          <div className="admin-user-detail-tabs">
            <button
              className={`admin-user-detail-tab ${activeTab === 'comprehension' ? 'admin-user-detail-tab--active' : ''}`}
              onClick={() => setActiveTab('comprehension')}
            >
              {t('userDetail.tabComprehension', 'Comprehension')}
              {(comprehensionState.total || activityCounts?.comprehension || 0) > 0 && <span className="admin-user-detail-tab-count">{comprehensionState.total || activityCounts?.comprehension || 0}</span>}
            </button>
            <button
              className={`admin-user-detail-tab ${activeTab === 'quiz' ? 'admin-user-detail-tab--active' : ''}`}
              onClick={() => setActiveTab('quiz')}
            >
              {t('userDetail.tabQuiz', 'Sentence Meaning')}
              {(quizState.total || activityCounts?.quiz || 0) > 0 && <span className="admin-user-detail-tab-count">{quizState.total || activityCounts?.quiz || 0}</span>}
            </button>
            <button
              className={`admin-user-detail-tab ${activeTab === 'shadowing' ? 'admin-user-detail-tab--active' : ''}`}
              onClick={() => setActiveTab('shadowing')}
            >
              {t('userDetail.tabShadowing', 'Shadowing')}
              {(shadowingState.total || activityCounts?.shadowing || 0) > 0 && <span className="admin-user-detail-tab-count">{shadowingState.total || activityCounts?.shadowing || 0}</span>}
            </button>
            <button
              className={`admin-user-detail-tab ${activeTab === 'dictation' ? 'admin-user-detail-tab--active' : ''}`}
              onClick={() => setActiveTab('dictation')}
            >
              {t('userDetail.tabDictation', 'Dictation')}
              {(dictationState.total || activityCounts?.dictation || 0) > 0 && <span className="admin-user-detail-tab-count">{dictationState.total || activityCounts?.dictation || 0}</span>}
            </button>
          </div>
        </div>

        {/* Sub-group: 字彙 */}
        <div className="admin-user-detail-tab-group">
          <div className="admin-user-detail-tab-group-label">
            {t('userDetail.groupVocabulary', 'Vocabulary')}
          </div>
          <div className="admin-user-detail-tabs">
            <button
              className={`admin-user-detail-tab ${activeTab === 'notes' ? 'admin-user-detail-tab--active' : ''}`}
              onClick={() => setActiveTab('notes')}
            >
              {t('userDetail.tabNotes', 'Notes')}
              {(notesState.total || activityCounts?.notes || 0) > 0 && <span className="admin-user-detail-tab-count">{notesState.total || activityCounts?.notes || 0}</span>}
            </button>
            <button
              className={`admin-user-detail-tab ${activeTab === 'savedVocab' ? 'admin-user-detail-tab--active' : ''}`}
              onClick={() => setActiveTab('savedVocab')}
            >
              {t('userDetail.tabSavedVocab', 'Vocabulary Book')}
              {(savedVocabState.total || activityCounts?.savedVocab || 0) > 0 && <span className="admin-user-detail-tab-count">{savedVocabState.total || activityCounts?.savedVocab || 0}</span>}
            </button>
            <button
              className={`admin-user-detail-tab ${activeTab === 'sightWord' ? 'admin-user-detail-tab--active' : ''}`}
              onClick={() => setActiveTab('sightWord')}
            >
              {t('userDetail.tabSightWord', 'Sight Word Test')}
              {(sightWordState.total || activityCounts?.sightWord || 0) > 0 && <span className="admin-user-detail-tab-count">{sightWordState.total || activityCounts?.sightWord || 0}</span>}
            </button>
          </div>
        </div>

        {/* Sub-group: 進階 */}
        <div className="admin-user-detail-tab-group">
          <div className="admin-user-detail-tab-group-label">
            {t('userDetail.groupAdvanced', 'Advanced')}
          </div>
          <div className="admin-user-detail-tabs">
            <button
              className={`admin-user-detail-tab ${activeTab === 'chat' ? 'admin-user-detail-tab--active' : ''}`}
              onClick={() => setActiveTab('chat')}
            >
              {t('userDetail.tabChat', 'AI Chat')}
              {(chatState.total || activityCounts?.chat || 0) > 0 && <span className="admin-user-detail-tab-count">{chatState.total || activityCounts?.chat || 0}</span>}
            </button>
            <button
              className={`admin-user-detail-tab ${activeTab === 'writing' ? 'admin-user-detail-tab--active' : ''}`}
              onClick={() => setActiveTab('writing')}
            >
              {t('userDetail.tabWriting', 'Writing Test')}
              {(writingState.total || activityCounts?.writing || 0) > 0 && <span className="admin-user-detail-tab-count">{writingState.total || activityCounts?.writing || 0}</span>}
            </button>
          </div>
        </div>

        {activeTab === 'notes' && (
          <div className="admin-user-detail-tab-panel">
            {notesState.loading ? (
              <div className="admin-user-detail-loading"><div className="admin-user-detail-spinner" /></div>
            ) : notesState.error ? (
              <p className="admin-user-detail-empty-text">{notesState.error}</p>
            ) : notesState.items.length === 0 ? (
              <p className="admin-user-detail-empty-text">{t('userDetail.noNotes', 'No notes yet')}</p>
            ) : (
              <>
                <div className="admin-user-detail-table-wrap">
                  <table className="admin-user-detail-table">
                    <thead>
                      <tr>
                        <th>{t('userDetail.date', 'Date')}</th>
                        <th>{t('userDetail.course', 'Course')}</th>
                        <th>{t('userDetail.associationType', 'Type')}</th>
                        <th>{t('userDetail.content', 'Content')}</th>
                        <th>{t('userDetail.context', 'Context')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notesState.items.map((n) => (
                        <tr key={n.id}>
                          <td>{formatDate(n.createdAt)}</td>
                          <td>{n.courseTitle || '--'}</td>
                          <td>
                            <span className="admin-user-detail-badge admin-user-detail-badge--gray">
                              {n.associationType}
                            </span>
                          </td>
                          <td className="admin-user-detail-note-content">{n.content}</td>
                          <td className="admin-user-detail-note-context">
                            {n.vocabularyWord || n.phraseText || (n.timestamp !== null ? `@${n.timestamp}s` : '--')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {notesState.total > notesState.limit && (
                  <div className="admin-user-detail-pager">
                    <button
                      disabled={notesState.page <= 1 || notesState.loading}
                      onClick={() => fetchNotes(notesState.page - 1)}
                      className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                    >
                      {t('common.prev', 'Prev')}
                    </button>
                    <span className="admin-user-detail-pager-info">
                      {t('userDetail.pageInfo', 'Page {{page}} of {{total}}', {
                        page: notesState.page,
                        total: Math.max(1, Math.ceil(notesState.total / notesState.limit)),
                      })}
                    </span>
                    <button
                      disabled={notesState.page * notesState.limit >= notesState.total || notesState.loading}
                      onClick={() => fetchNotes(notesState.page + 1)}
                      className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                    >
                      {t('common.next', 'Next')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'shadowing' && (
          <div className="admin-user-detail-tab-panel">
            {shadowingState.loading ? (
              <div className="admin-user-detail-loading"><div className="admin-user-detail-spinner" /></div>
            ) : shadowingState.error ? (
              <p className="admin-user-detail-empty-text">{shadowingState.error}</p>
            ) : shadowingState.items.length === 0 ? (
              <p className="admin-user-detail-empty-text">{t('userDetail.noShadowing', 'No shadowing attempts yet')}</p>
            ) : (
              <>
                <div className="admin-user-detail-table-wrap">
                  <table className="admin-user-detail-table">
                    <thead>
                      <tr>
                        <th>{t('userDetail.date', 'Date')}</th>
                        <th>{t('userDetail.course', 'Course')}</th>
                        <th>{t('userDetail.sentence', 'Sentence')}</th>
                        <th>{t('userDetail.mode', 'Mode')}</th>
                        <th>{t('userDetail.overall', 'Overall')}</th>
                        <th>{t('userDetail.accuracy', 'Accuracy')}</th>
                        <th>{t('userDetail.fluency', 'Fluency')}</th>
                        <th>{t('userDetail.recording', 'Audio')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shadowingState.items.map((s) => (
                        <tr key={s.id}>
                          <td>{formatDate(s.createdAt)}</td>
                          <td>{s.courseTitle || '--'}</td>
                          <td className="admin-user-detail-note-content">{s.sentenceText}</td>
                          <td>{s.mode}</td>
                          <td>{s.overallScore.toFixed(1)}</td>
                          <td>{s.accuracyScore.toFixed(1)}</td>
                          <td>{s.fluencyScore.toFixed(1)}</td>
                          <td>
                            {s.recordingUrl ? (
                              <audio controls src={s.recordingUrl} style={{ height: 28 }} />
                            ) : (
                              <span className="admin-user-detail-text-red">--</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {shadowingState.total > shadowingState.limit && (
                  <div className="admin-user-detail-pager">
                    <button
                      disabled={shadowingState.page <= 1 || shadowingState.loading}
                      onClick={() => fetchShadowing(shadowingState.page - 1)}
                      className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                    >
                      {t('common.prev', 'Prev')}
                    </button>
                    <span className="admin-user-detail-pager-info">
                      {t('userDetail.pageInfo', 'Page {{page}} of {{total}}', {
                        page: shadowingState.page,
                        total: Math.max(1, Math.ceil(shadowingState.total / shadowingState.limit)),
                      })}
                    </span>
                    <button
                      disabled={shadowingState.page * shadowingState.limit >= shadowingState.total || shadowingState.loading}
                      onClick={() => fetchShadowing(shadowingState.page + 1)}
                      className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                    >
                      {t('common.next', 'Next')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'dictation' && (
          <div className="admin-user-detail-tab-panel">
            {dictationState.loading ? (
              <div className="admin-user-detail-loading"><div className="admin-user-detail-spinner" /></div>
            ) : dictationState.error ? (
              <p className="admin-user-detail-empty-text">{dictationState.error}</p>
            ) : dictationState.items.length === 0 ? (
              <p className="admin-user-detail-empty-text">{t('userDetail.noDictation', 'No dictation attempts yet')}</p>
            ) : (
              <>
                <div className="admin-user-detail-table-wrap">
                  <table className="admin-user-detail-table">
                    <thead>
                      <tr>
                        <th>{t('userDetail.date', 'Date')}</th>
                        <th>{t('userDetail.course', 'Course')}</th>
                        <th>{t('userDetail.expected', 'Expected')}</th>
                        <th>{t('userDetail.userInput', 'User Input')}</th>
                        <th>{t('userDetail.result', 'Result')}</th>
                        <th>{t('userDetail.inputMode', 'Mode')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dictationState.items.map((d) => (
                        <tr key={d.id}>
                          <td>{formatDate(d.createdAt)}</td>
                          <td>{d.courseTitle || '--'}</td>
                          <td>{d.expectedWord}</td>
                          <td className={d.isCorrect ? 'admin-user-detail-text-green' : 'admin-user-detail-text-red'}>
                            {d.userInput || <em>(empty)</em>}
                          </td>
                          <td>
                            <span className={`admin-user-detail-badge admin-user-detail-badge--${d.isCorrect ? 'green' : 'red'}`}>
                              {d.isCorrect ? t('userDetail.correct', 'Correct') : t('userDetail.wrong', 'Wrong')}
                            </span>
                          </td>
                          <td>{d.inputMode}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {dictationState.total > dictationState.limit && (
                  <div className="admin-user-detail-pager">
                    <button
                      disabled={dictationState.page <= 1 || dictationState.loading}
                      onClick={() => fetchDictation(dictationState.page - 1)}
                      className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                    >
                      {t('common.prev', 'Prev')}
                    </button>
                    <span className="admin-user-detail-pager-info">
                      {t('userDetail.pageInfo', 'Page {{page}} of {{total}}', {
                        page: dictationState.page,
                        total: Math.max(1, Math.ceil(dictationState.total / dictationState.limit)),
                      })}
                    </span>
                    <button
                      disabled={dictationState.page * dictationState.limit >= dictationState.total || dictationState.loading}
                      onClick={() => fetchDictation(dictationState.page + 1)}
                      className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                    >
                      {t('common.next', 'Next')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'comprehension' && (
          <div className="admin-user-detail-tab-panel">
            {comprehensionState.loading ? (
              <div className="admin-user-detail-loading"><div className="admin-user-detail-spinner" /></div>
            ) : comprehensionState.error ? (
              <p className="admin-user-detail-empty-text">{comprehensionState.error}</p>
            ) : comprehensionState.items.length === 0 ? (
              <p className="admin-user-detail-empty-text">{t('userDetail.noComprehension', 'No comprehension activity yet')}</p>
            ) : (
              <>
                <div className="admin-user-detail-table-wrap">
                  <table className="admin-user-detail-table">
                    <thead>
                      <tr>
                        <th>{t('userDetail.translationDate', 'Date')}</th>
                        <th>{t('userDetail.course', 'Course')}</th>
                        <th>{t('userDetail.translationSentenceIdx', '#')}</th>
                        <th>{t('userDetail.translationOriginal', 'Original')}</th>
                        <th>{t('userDetail.translationUserAnswer', 'User Translation')}</th>
                        <th>{t('userDetail.translationReference', 'Reference')}</th>
                        <th>{t('userDetail.translationScore', 'Score')}</th>
                        <th>{t('userDetail.translationFeedback', 'Feedback')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comprehensionState.items.map((c) => (
                        <tr key={c.id}>
                          <td>{formatDateTime(c.createdAt)}</td>
                          <td>{c.courseTitle || '--'}</td>
                          <td>#{c.sentenceIndex}</td>
                          <td className="admin-user-detail-cell-wrap">{c.originalSentence}</td>
                          <td className={`admin-user-detail-cell-wrap ${c.isCorrect ? 'admin-user-detail-text-green' : 'admin-user-detail-text-red'}`}>
                            {c.userTranslation}
                          </td>
                          <td className="admin-user-detail-cell-wrap admin-user-detail-text-muted">{c.correctTranslation}</td>
                          <td>
                            <span className={`admin-user-detail-badge admin-user-detail-badge--${c.overallScore >= 80 ? 'green' : c.overallScore >= 50 ? 'yellow' : 'red'}`}>
                              {c.overallScore}
                            </span>
                          </td>
                          <td className="admin-user-detail-cell-wrap admin-user-detail-text-muted">
                            {c.feedback}
                            {c.errors.length > 0 && (
                              <ul className="admin-user-detail-error-list">
                                {c.errors.map((e, i) => (
                                  <li key={i} className={`admin-user-detail-error admin-user-detail-error--${e.type}`}>
                                    <strong>{e.type}:</strong> {e.description}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {comprehensionState.total > comprehensionState.limit && (
                  <div className="admin-user-detail-pager">
                    <button
                      disabled={comprehensionState.page <= 1 || comprehensionState.loading}
                      onClick={() => fetchComprehension(comprehensionState.page - 1)}
                      className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                    >
                      {t('common.prev', 'Prev')}
                    </button>
                    <span className="admin-user-detail-pager-info">
                      {t('userDetail.pageInfo', 'Page {{page}} of {{total}}', {
                        page: comprehensionState.page,
                        total: Math.max(1, Math.ceil(comprehensionState.total / comprehensionState.limit)),
                      })}
                    </span>
                    <button
                      disabled={comprehensionState.page * comprehensionState.limit >= comprehensionState.total || comprehensionState.loading}
                      onClick={() => fetchComprehension(comprehensionState.page + 1)}
                      className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                    >
                      {t('common.next', 'Next')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'quiz' && (
          <div className="admin-user-detail-tab-panel">
            {quizState.loading ? (
              <div className="admin-user-detail-loading"><div className="admin-user-detail-spinner" /></div>
            ) : quizState.error ? (
              <p className="admin-user-detail-empty-text">{quizState.error}</p>
            ) : quizState.items.length === 0 ? (
              <p className="admin-user-detail-empty-text">{t('userDetail.noQuiz', 'No sentence-meaning activity yet')}</p>
            ) : (
              <>
                <div className="admin-user-detail-table-wrap">
                  <table className="admin-user-detail-table">
                    <thead>
                      <tr>
                        <th>{t('userDetail.quizDate', 'Date')}</th>
                        <th>{t('userDetail.course', 'Course')}</th>
                        <th>{t('userDetail.quizQuestionId', 'Question')}</th>
                        <th>{t('userDetail.quizQuestionType', 'Type')}</th>
                        <th>{t('userDetail.quizResult', 'Result')}</th>
                        <th>{t('userDetail.quizRelatedWords', 'Related Words')}</th>
                        <th>{t('userDetail.quizTime', 'Time')}</th>
                        <th>{t('userDetail.quizBatchScore', 'Batch Score')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quizState.items.map((q) => (
                        <tr key={q.id}>
                          <td>{formatDateTime(q.createdAt)}</td>
                          <td>{q.courseTitle || '--'}</td>
                          <td className="admin-user-detail-cell-mono">{q.questionId}</td>
                          <td>{q.questionType}</td>
                          <td>
                            <span className={`admin-user-detail-badge admin-user-detail-badge--${q.isCorrect ? 'green' : 'red'}`}>
                              {q.isCorrect ? t('common.correct', 'Correct') : t('common.wrong', 'Wrong')}
                            </span>
                          </td>
                          <td className="admin-user-detail-cell-wrap">{q.relatedWords?.join(', ') || '--'}</td>
                          <td>{q.timeSpentSeconds != null ? `${q.timeSpentSeconds}s` : '--'}</td>
                          <td>
                            {q.batchTotalQuestions != null
                              ? `${q.batchCorrectAnswers ?? 0}/${q.batchTotalQuestions}`
                              : '--'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {quizState.total > quizState.limit && (
                  <div className="admin-user-detail-pager">
                    <button
                      disabled={quizState.page <= 1 || quizState.loading}
                      onClick={() => fetchQuiz(quizState.page - 1)}
                      className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                    >
                      {t('common.prev', 'Prev')}
                    </button>
                    <span className="admin-user-detail-pager-info">
                      {t('userDetail.pageInfo', 'Page {{page}} of {{total}}', {
                        page: quizState.page,
                        total: Math.max(1, Math.ceil(quizState.total / quizState.limit)),
                      })}
                    </span>
                    <button
                      disabled={quizState.page * quizState.limit >= quizState.total || quizState.loading}
                      onClick={() => fetchQuiz(quizState.page + 1)}
                      className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                    >
                      {t('common.next', 'Next')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'savedVocab' && (
          <div className="admin-user-detail-tab-panel">
            {savedVocabState.loading ? (
              <div className="admin-user-detail-loading"><div className="admin-user-detail-spinner" /></div>
            ) : savedVocabState.error ? (
              <p className="admin-user-detail-empty-text">{savedVocabState.error}</p>
            ) : savedVocabState.items.length === 0 ? (
              <p className="admin-user-detail-empty-text">{t('userDetail.noSavedVocab', 'No saved vocabulary yet')}</p>
            ) : (
              <>
                <div className="admin-user-detail-table-wrap">
                  <table className="admin-user-detail-table">
                    <thead>
                      <tr>
                        <th>{t('userDetail.word', 'Word')}</th>
                        <th>{t('userDetail.lemma', 'Lemma')}</th>
                        <th>{t('userDetail.encounteredForm', 'Form')}</th>
                        <th>{t('userDetail.masteryLevel', 'Mastery')}</th>
                        <th>{t('userDetail.reps', 'Reps')}</th>
                        <th>{t('userDetail.lapses', 'Lapses')}</th>
                        <th>{t('userDetail.due', 'Due')}</th>
                        <th>{t('userDetail.lastReviewed', 'Last Reviewed')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {savedVocabState.items.map((v) => (
                        <tr key={v.id}>
                          <td><strong>{v.word || '--'}</strong>{v.isFavorite && ' ★'}</td>
                          <td>{v.lemma || '--'}</td>
                          <td>{v.encounteredForm || '--'}</td>
                          <td>{v.masteryLevel}/5</td>
                          <td>{v.reps}</td>
                          <td>{v.lapses}</td>
                          <td>{formatDate(v.due || undefined)}</td>
                          <td>{formatDate(v.lastReviewedAt || undefined)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {savedVocabState.total > savedVocabState.limit && (
                  <div className="admin-user-detail-pager">
                    <button
                      disabled={savedVocabState.page <= 1 || savedVocabState.loading}
                      onClick={() => fetchSavedVocab(savedVocabState.page - 1)}
                      className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                    >
                      {t('common.prev', 'Prev')}
                    </button>
                    <span className="admin-user-detail-pager-info">
                      {t('userDetail.pageInfo', 'Page {{page}} of {{total}}', {
                        page: savedVocabState.page,
                        total: Math.max(1, Math.ceil(savedVocabState.total / savedVocabState.limit)),
                      })}
                    </span>
                    <button
                      disabled={savedVocabState.page * savedVocabState.limit >= savedVocabState.total || savedVocabState.loading}
                      onClick={() => fetchSavedVocab(savedVocabState.page + 1)}
                      className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                    >
                      {t('common.next', 'Next')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'sightWord' && (
          <div className="admin-user-detail-tab-panel">
            {sightWordState.loading ? (
              <div className="admin-user-detail-loading"><div className="admin-user-detail-spinner" /></div>
            ) : sightWordState.error ? (
              <p className="admin-user-detail-empty-text">{sightWordState.error}</p>
            ) : sightWordState.items.length === 0 ? (
              <p className="admin-user-detail-empty-text">{t('userDetail.noSightWord', 'No sight word activity yet')}</p>
            ) : (
              <>
                <div className="admin-user-detail-table-wrap">
                  <table className="admin-user-detail-table">
                    <thead>
                      <tr>
                        <th>{t('userDetail.word', 'Word')}</th>
                        <th>{t('userDetail.correct', 'Correct')}</th>
                        <th>{t('userDetail.errors', 'Errors')}</th>
                        <th>{t('userDetail.totalAttempts', 'Attempts')}</th>
                        <th>{t('userDetail.accuracy', 'Accuracy')}</th>
                        <th>{t('userDetail.lastSeen', 'Last Seen')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sightWordState.items.map((s) => (
                        <tr key={s.id}>
                          <td><strong>{s.word}</strong></td>
                          <td className="admin-user-detail-text-green">{s.correctCount}</td>
                          <td className={s.errorCount > 0 ? 'admin-user-detail-text-red' : ''}>{s.errorCount}</td>
                          <td>{s.totalAttempts}</td>
                          <td>{s.accuracy !== null ? `${(s.accuracy * 100).toFixed(0)}%` : '--'}</td>
                          <td>{formatDate(s.lastSeenAt || undefined)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {sightWordState.total > sightWordState.limit && (
                  <div className="admin-user-detail-pager">
                    <button
                      disabled={sightWordState.page <= 1 || sightWordState.loading}
                      onClick={() => fetchSightWord(sightWordState.page - 1)}
                      className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                    >
                      {t('common.prev', 'Prev')}
                    </button>
                    <span className="admin-user-detail-pager-info">
                      {t('userDetail.pageInfo', 'Page {{page}} of {{total}}', {
                        page: sightWordState.page,
                        total: Math.max(1, Math.ceil(sightWordState.total / sightWordState.limit)),
                      })}
                    </span>
                    <button
                      disabled={sightWordState.page * sightWordState.limit >= sightWordState.total || sightWordState.loading}
                      onClick={() => fetchSightWord(sightWordState.page + 1)}
                      className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                    >
                      {t('common.next', 'Next')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'chat' && (
          <div className="admin-user-detail-tab-panel">
            {chatState.loading ? (
              <div className="admin-user-detail-loading"><div className="admin-user-detail-spinner" /></div>
            ) : chatState.error ? (
              <p className="admin-user-detail-empty-text">{chatState.error}</p>
            ) : chatState.items.length === 0 ? (
              <p className="admin-user-detail-empty-text">{t('userDetail.noChat', 'No AI chat activity yet')}</p>
            ) : (
              <>
                <div className="admin-user-detail-table-wrap">
                  <table className="admin-user-detail-table">
                    <thead>
                      <tr>
                        <th>{t('userDetail.persona', 'Persona')}</th>
                        <th>{t('userDetail.familiarity', 'Familiarity')}</th>
                        <th>{t('userDetail.userMessages', 'User Msgs')}</th>
                        <th>{t('userDetail.botMessages', 'Bot Msgs')}</th>
                        <th>{t('userDetail.totalMessages', 'Total')}</th>
                        <th>{t('userDetail.lastUserMessage', 'Last Msg')}</th>
                        <th>{t('userDetail.firstChatAt', 'Started')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chatState.items.map((c) => (
                        <tr key={c.id}>
                          <td>
                            <strong>{c.personaName || c.botUsername || '--'}</strong>
                            {c.personaType && <div style={{ fontSize: '0.75rem', color: '#888' }}>{c.personaType}</div>}
                          </td>
                          <td>
                            <span className="admin-user-detail-badge admin-user-detail-badge--purple">
                              {c.familiarityLevel}
                            </span>
                          </td>
                          <td>{c.userMessageCount}</td>
                          <td>{c.botMessageCount}</td>
                          <td>{c.messageCount}</td>
                          <td>{formatDateTime(c.lastUserMessageAt || undefined)}</td>
                          <td>{formatDate(c.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {chatState.total > chatState.limit && (
                  <div className="admin-user-detail-pager">
                    <button
                      disabled={chatState.page <= 1 || chatState.loading}
                      onClick={() => fetchChat(chatState.page - 1)}
                      className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                    >
                      {t('common.prev', 'Prev')}
                    </button>
                    <span className="admin-user-detail-pager-info">
                      {t('userDetail.pageInfo', 'Page {{page}} of {{total}}', {
                        page: chatState.page,
                        total: Math.max(1, Math.ceil(chatState.total / chatState.limit)),
                      })}
                    </span>
                    <button
                      disabled={chatState.page * chatState.limit >= chatState.total || chatState.loading}
                      onClick={() => fetchChat(chatState.page + 1)}
                      className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                    >
                      {t('common.next', 'Next')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'writing' && (
          <div className="admin-user-detail-tab-panel">
            {writingState.loading ? (
              <div className="admin-user-detail-loading"><div className="admin-user-detail-spinner" /></div>
            ) : writingState.error ? (
              <p className="admin-user-detail-empty-text">{writingState.error}</p>
            ) : writingState.items.length === 0 ? (
              <p className="admin-user-detail-empty-text">{t('userDetail.noWriting', 'No writing sessions yet')}</p>
            ) : (
              <>
                <div className="admin-user-detail-table-wrap">
                  <table className="admin-user-detail-table">
                    <thead>
                      <tr>
                        <th>{t('userDetail.startedAt', 'Started')}</th>
                        <th>{t('userDetail.course', 'Course')}</th>
                        <th>{t('userDetail.writingType', 'Type')}</th>
                        <th>{t('userDetail.status', 'Status')}</th>
                        <th>{t('userDetail.gate', 'Gate')}</th>
                        <th>{t('userDetail.score', 'Score')}</th>
                        <th>{t('userDetail.points', 'Points')}</th>
                        <th>{t('userDetail.tokens', 'Tokens')}</th>
                        <th>{t('userDetail.timeSpent', 'Time')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {writingState.items.map((w) => (
                        <tr key={w.id}>
                          <td>{formatDateTime(w.startedAt)}</td>
                          <td>{w.courseTitle || w.scenarioTheme || '--'}</td>
                          <td>{w.type}</td>
                          <td>
                            <span className={`admin-user-detail-badge admin-user-detail-badge--${w.status === 'completed' ? 'green' : w.status === 'abandoned' ? 'red' : 'yellow'}`}>
                              {w.status}
                            </span>
                            {w.isPerfect && ' ★'}
                          </td>
                          <td>{w.currentGate}/4</td>
                          <td>{w.totalScore}</td>
                          <td>{w.pointsEarned}</td>
                          <td>{w.tokensConsumed}</td>
                          <td>{formatLearningTime(w.timeSpentSeconds)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {writingState.total > writingState.limit && (
                  <div className="admin-user-detail-pager">
                    <button
                      disabled={writingState.page <= 1 || writingState.loading}
                      onClick={() => fetchWriting(writingState.page - 1)}
                      className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                    >
                      {t('common.prev', 'Prev')}
                    </button>
                    <span className="admin-user-detail-pager-info">
                      {t('userDetail.pageInfo', 'Page {{page}} of {{total}}', {
                        page: writingState.page,
                        total: Math.max(1, Math.ceil(writingState.total / writingState.limit)),
                      })}
                    </span>
                    <button
                      disabled={writingState.page * writingState.limit >= writingState.total || writingState.loading}
                      onClick={() => fetchWriting(writingState.page + 1)}
                      className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                    >
                      {t('common.next', 'Next')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
          </>
        )}
      </div>

      {/* Section: 客服與系統 */}
      <div className="admin-user-detail-section-header">
        {t('userDetail.sectionSupportSystem', 'Support & System')}
      </div>

      {/* Support Actions Card - full width */}
      <div className="admin-user-detail-card admin-user-detail-card--full">
        <div className="admin-user-detail-card-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          {t('userDetail.supportActions', 'Support Actions')}
        </div>
        <div className="admin-user-detail-support-grid">
          <div className="admin-user-detail-support-row">
            <div className="admin-user-detail-support-info">
              <div className="admin-user-detail-support-label">
                {t('userDetail.resendWeeklyReport', 'Resend Weekly Report')}
              </div>
              <div className="admin-user-detail-support-desc">
                {t(
                  'userDetail.resendWeeklyReportDesc',
                  'Regenerate this week\'s AI learning report and email it to the user.',
                )}
              </div>
              {supportStatus.weeklyReport.state === 'success' && (
                <div className="admin-user-detail-support-status admin-user-detail-support-status--success">
                  {supportStatus.weeklyReport.message}
                </div>
              )}
              {supportStatus.weeklyReport.state === 'error' && (
                <div className="admin-user-detail-support-status admin-user-detail-support-status--error">
                  {supportStatus.weeklyReport.message}
                </div>
              )}
            </div>
            <button
              className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
              onClick={handleResendWeeklyReport}
              disabled={supportStatus.weeklyReport.state === 'sending'}
            >
              {supportStatus.weeklyReport.state === 'sending'
                ? t('common.sending', 'Sending…')
                : t('userDetail.resend', 'Resend')}
            </button>
          </div>

          <div className="admin-user-detail-support-row">
            <div className="admin-user-detail-support-info">
              <div className="admin-user-detail-support-label">
                {t('userDetail.sendPasswordReset', 'Send Password Reset Email')}
              </div>
              <div className="admin-user-detail-support-desc">
                {t(
                  'userDetail.sendPasswordResetDesc',
                  'Trigger a password reset email to the user\'s registered address. Token valid for 1 hour.',
                )}
              </div>
              {supportStatus.passwordReset.state === 'success' && (
                <div className="admin-user-detail-support-status admin-user-detail-support-status--success">
                  {supportStatus.passwordReset.message}
                </div>
              )}
              {supportStatus.passwordReset.state === 'error' && (
                <div className="admin-user-detail-support-status admin-user-detail-support-status--error">
                  {supportStatus.passwordReset.message}
                </div>
              )}
            </div>
            <button
              className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
              onClick={handleSendPasswordReset}
              disabled={supportStatus.passwordReset.state === 'sending'}
            >
              {supportStatus.passwordReset.state === 'sending'
                ? t('common.sending', 'Sending…')
                : t('userDetail.send', 'Send')}
            </button>
          </div>

          <div className="admin-user-detail-support-row">
            <div className="admin-user-detail-support-info">
              <div className="admin-user-detail-support-label">
                {t('userDetail.sendVerificationEmail', 'Resend Verification Email')}
                {user.emailVerified && (
                  <span className="admin-user-detail-badge admin-user-detail-badge--green" style={{ marginLeft: 8 }}>
                    {t('userDetail.verified', 'Verified')}
                  </span>
                )}
              </div>
              <div className="admin-user-detail-support-desc">
                {user.emailVerified
                  ? t('userDetail.alreadyVerified', 'This email is already verified — no action needed.')
                  : t(
                      'userDetail.sendVerificationEmailDesc',
                      'Send a fresh email verification link. Token valid for 24 hours.',
                    )}
              </div>
              {supportStatus.verifyEmail.state === 'success' && (
                <div className="admin-user-detail-support-status admin-user-detail-support-status--success">
                  {supportStatus.verifyEmail.message}
                </div>
              )}
              {supportStatus.verifyEmail.state === 'error' && (
                <div className="admin-user-detail-support-status admin-user-detail-support-status--error">
                  {supportStatus.verifyEmail.message}
                </div>
              )}
            </div>
            <button
              className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
              onClick={handleSendVerificationEmail}
              disabled={supportStatus.verifyEmail.state === 'sending' || !!user.emailVerified}
            >
              {supportStatus.verifyEmail.state === 'sending'
                ? t('common.sending', 'Sending…')
                : t('userDetail.send', 'Send')}
            </button>
          </div>
        </div>
      </div>

      {/* Activity Card 2 - 系統 (audit) */}
      <div className="admin-user-detail-card admin-user-detail-card--full">
        <div className="admin-user-detail-card-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
          </svg>
          {t('userDetail.cardSystem', 'System')}
          <span style={{ marginLeft: 12, fontSize: '0.85rem', color: '#888', fontWeight: 'normal' }}>
            · {t('userDetail.tabAudit', 'Audit')}
            {auditState.total > 0 && ` (${auditState.total})`}
          </span>
        </div>
        <div className="admin-user-detail-tab-panel">
          {auditState.loading ? (
            <div className="admin-user-detail-loading"><div className="admin-user-detail-spinner" /></div>
          ) : auditState.error ? (
            <p className="admin-user-detail-empty-text">{auditState.error}</p>
          ) : auditState.items.length === 0 ? (
            <p className="admin-user-detail-empty-text">
              {t('userDetail.noAudit', 'No admin actions recorded for this user.')}
            </p>
          ) : (
            <>
              <div className="admin-user-detail-table-wrap">
                <table className="admin-user-detail-table">
                  <thead>
                    <tr>
                      <th>{t('userDetail.date', 'Date')}</th>
                      <th>{t('userDetail.auditAction', 'Action')}</th>
                      <th>{t('userDetail.auditAdmin', 'Admin')}</th>
                      <th>{t('userDetail.auditReason', 'Reason')}</th>
                      <th>{t('userDetail.auditMetadata', 'Metadata')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditState.items.map((a) => (
                      <tr key={a.id}>
                        <td>{formatDateTime(a.created_at)}</td>
                        <td><code>{a.action}</code></td>
                        <td title={a.admin_id}>{a.admin_id.substring(0, 8)}…</td>
                        <td className="admin-user-detail-note-content">{a.reason || '--'}</td>
                        <td className="admin-user-detail-note-context">
                          {a.metadata ? (
                            <details>
                              <summary style={{ cursor: 'pointer', color: '#667eea' }}>
                                {t('userDetail.viewMetadata', 'View')}
                              </summary>
                              <pre style={{ margin: '6px 0 0', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {JSON.stringify(a.metadata, null, 2)}
                              </pre>
                            </details>
                          ) : '--'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {auditState.total > auditState.limit && (
                <div className="admin-user-detail-pager">
                  <button
                    disabled={auditState.page <= 1 || auditState.loading}
                    onClick={() => fetchAudit(auditState.page - 1)}
                    className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                  >
                    {t('common.prev', 'Prev')}
                  </button>
                  <span className="admin-user-detail-pager-info">
                    {t('userDetail.pageInfo', 'Page {{page}} of {{total}}', {
                      page: auditState.page,
                      total: Math.max(1, Math.ceil(auditState.total / auditState.limit)),
                    })}
                  </span>
                  <button
                    disabled={auditState.page * auditState.limit >= auditState.total || auditState.loading}
                    onClick={() => fetchAudit(auditState.page + 1)}
                    className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                  >
                    {t('common.next', 'Next')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Modals ── */}

      {/* Change Role Modal */}
      {activeModal === 'role' && (
        <div className="admin-user-detail-overlay" onClick={() => setActiveModal(null)}>
          <div className="admin-user-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-user-detail-modal-title">{t('userDetail.changeRole', 'Change Role')}</div>
            <p className="admin-user-detail-modal-desc">
              {t('userDetail.changeRoleDesc', 'Select a new role for this user.')}
            </p>
            <select
              className="admin-user-detail-modal-select"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              <option value="user">{t('userDetail.roleUser', 'User')}</option>
              <option value="support">{t('userDetail.roleSupport', 'Support (non-destructive admin ops)')}</option>
              <option value="admin">{t('userDetail.roleAdmin', 'Admin (full access)')}</option>
            </select>
            <div className="admin-user-detail-modal-actions">
              <button
                className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                onClick={() => setActiveModal(null)}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                className="admin-user-detail-action-btn admin-user-detail-action-btn--primary"
                onClick={handleChangeRole}
                disabled={actionLoading || selectedRole === user.role}
              >
                {actionLoading ? t('common.saving', 'Saving...') : t('common.confirm', 'Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Account Type Modal */}
      {activeModal === 'accountType' && (
        <div className="admin-user-detail-overlay" onClick={() => setActiveModal(null)}>
          <div className="admin-user-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-user-detail-modal-title">
              {t('userDetail.changeAccountType', 'Account Type')}
            </div>
            <p className="admin-user-detail-modal-desc">
              {t(
                'userDetail.changeAccountTypeDesc',
                'Non-regular accounts bypass the paywall and are excluded from all KPI/revenue aggregations.',
              )}
            </p>
            <div className="admin-user-detail-modal-field">
              <label>{t('userDetail.accountType', 'Account Type')}</label>
              <select
                className="admin-user-detail-modal-select"
                value={selectedAccountType}
                onChange={(e) => setSelectedAccountType(e.target.value)}
              >
                <option value="regular">Regular (counts toward KPI)</option>
                <option value="internal">Internal (company test account)</option>
                <option value="comp">Comp (complimentary / partner)</option>
                <option value="beta_tester">Beta Tester</option>
              </select>
            </div>
            <div className="admin-user-detail-modal-field">
              <label>{t('userDetail.reason', 'Reason')} <small>({t('common.optional', 'optional')})</small></label>
              <textarea
                className="admin-user-detail-modal-textarea"
                placeholder={t('userDetail.accountTypeReasonPlaceholder', 'Why is this change being made?')}
                value={accountTypeReason}
                onChange={(e) => setAccountTypeReason(e.target.value)}
                rows={2}
              />
            </div>
            <div className="admin-user-detail-modal-actions">
              <button
                className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                onClick={() => setActiveModal(null)}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                className="admin-user-detail-action-btn admin-user-detail-action-btn--primary"
                onClick={handleChangeAccountType}
                disabled={actionLoading || selectedAccountType === (user.accountType || 'regular')}
              >
                {actionLoading ? t('common.saving', 'Saving...') : t('common.confirm', 'Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend Modal */}
      {activeModal === 'suspend' && (
        <div className="admin-user-detail-overlay" onClick={() => setActiveModal(null)}>
          <div className="admin-user-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-user-detail-modal-title">{t('userDetail.suspendUser', 'Suspend User')}</div>
            <p className="admin-user-detail-modal-desc">
              {t('userDetail.suspendDesc', 'Please provide a reason for suspending this user.')}
            </p>
            <textarea
              className="admin-user-detail-modal-textarea"
              placeholder={t('userDetail.suspendReasonPlaceholder', 'Reason for suspension...')}
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              rows={3}
            />
            <div className="admin-user-detail-modal-actions">
              <button
                className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                onClick={() => setActiveModal(null)}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                className="admin-user-detail-action-btn admin-user-detail-action-btn--red"
                onClick={handleSuspend}
                disabled={actionLoading || suspendReason.trim().length < 3}
              >
                {actionLoading ? t('common.saving', 'Saving...') : t('userDetail.suspend', 'Suspend')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Token Adjustment Modal */}
      {activeModal === 'tokens' && (
        <div className="admin-user-detail-overlay" onClick={() => setActiveModal(null)}>
          <div className="admin-user-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-user-detail-modal-title">{t('userDetail.adjustTokens', 'Adjust Tokens')}</div>
            <p className="admin-user-detail-modal-desc">
              {t('userDetail.adjustTokensDesc', 'Enter a positive number to add tokens, or negative to deduct.')}
            </p>
            <div className="admin-user-detail-modal-field">
              <label>{t('userDetail.amount', 'Amount')}</label>
              <input
                type="number"
                className="admin-user-detail-modal-input"
                placeholder="e.g. 100 or -50"
                value={tokenAmount}
                onChange={(e) => setTokenAmount(e.target.value)}
              />
            </div>
            <div className="admin-user-detail-modal-field">
              <label>{t('userDetail.reason', 'Reason')}</label>
              <textarea
                className="admin-user-detail-modal-textarea"
                placeholder={t('userDetail.tokenReasonPlaceholder', 'Reason for adjustment...')}
                value={tokenReason}
                onChange={(e) => setTokenReason(e.target.value)}
                rows={2}
              />
            </div>
            <div className="admin-user-detail-modal-actions">
              <button
                className="admin-user-detail-action-btn admin-user-detail-action-btn--outline"
                onClick={() => setActiveModal(null)}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                className="admin-user-detail-action-btn admin-user-detail-action-btn--primary"
                onClick={handleAdjustTokens}
                disabled={actionLoading || !tokenAmount || parseInt(tokenAmount, 10) === 0 || !tokenReason.trim()}
              >
                {actionLoading ? t('common.saving', 'Saving...') : t('common.confirm', 'Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserDetailPage;
